import axios, { AxiosInstance } from 'axios'
import * as Sentry from '@sentry/react'
import {
	CitiesByCategoryParams,
	CmsApiEndpoints,
	CmsAppFeatureData,
	CmsAppPromoData,
	CmsBannersData,
	CmsCitiesByCategoryData,
	CmsDiscoverSalonData,
	CmsDiscoverServiceData,
	CmsHeroData,
	CmsHomeMetadata,
	CmsHomepageData,
	CmsLoginAndRegistrationBannerData,
	CmsMyReservationPageData,
	CmsNpForBusinessData,
	CmsNpStatisticsData,
	CmsNpTestimonialsData,
	CmsParameterSelectionBannerData,
	CmsReservationBookingModalData,
	CmsReservationDetailData,
	CmsSalonListSmallBannerData,
	CmsSalonsCategoryPageData,
	CmsSalonsListPageData,
	CmsSalonsMetadata,
	CmsThankYouPageBannerData,
	CountryCode,
	RichTextBlockNode,
	SalonsCategoryMetaQueryParams,
	SalonsListMetaQueryParams
} from '../types/types'
import { getReq } from '../utils/request'
import {
	APP_FEATURE_FALLBACKS,
	APP_PROMO_FALLBACKS,
	BANNERS_FALLBACKS,
	DISCOVER_SALON_FALLBACKS,
	DISCOVER_SERVICE_FALLBACKS,
	HERO_SECTION_FALLBACKS,
	HOME_METADATA_FALLBACKS,
	LOGIN_AND_REGISTRATION_BANNER_FALLBACKS,
	NP_FOR_BUSINESSES_FALLBACKS,
	NP_STATISTICS_FALLBACKS,
	PARAMETER_SELECTION_BANNER_FALLBACKS,
	RESERVATION_DETAIL_BANNER_FALLBACKS,
	SALON_LIST_SMALL_BANNER_FALLBACKS,
	SALONS_CATEGORY_METADATA_FALLBACKS,
	SALONS_LIST_METADATA_FALLBACKS,
	TESTIMONIALS_FALLBACKS,
	THANK_YOU_PAGE_BANNER_FALLBACKS
} from '../utils/cmsFallbacks'

const getHeroSection = async (client: AxiosInstance): Promise<CmsHeroData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/hero-section', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data) {
			return HERO_SECTION_FALLBACKS()
		}

		return {
			title: result.data.data.title,
			subtitle: result.data.data.subtitle,
			background: {
				desktop: result.data.data.backgroundImage.desktopImage.url,
				mobile: result.data.data.backgroundImage.mobileImage.url,
				desktopWebP: result.data.data.backgroundImage.desktopImage.webpUrl || undefined,
				mobileWebP: result.data.data.backgroundImage.mobileImage.webpUrl || undefined
			}
		}
	} catch (error) {
		// Axios errors are captured centrally in the Axios interceptor with request context.
		// Keep CMS fallbacks resilient, and capture only non-Axios errors here.
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getHeroSection' } })
		}
		return HERO_SECTION_FALLBACKS()
	}
}

const getDiscoverSalon = async (client: AxiosInstance): Promise<CmsDiscoverSalonData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/discover-salon', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data) {
			return DISCOVER_SALON_FALLBACKS()
		}

		return {
			title: result.data.data.title,
			subtitle: result.data.data.subtitle,
			image: {
				desktop: result.data.data.image.desktopImage.url,
				desktopWebP: result.data.data.image.desktopImage.webpUrl || undefined,
				mobile: result.data.data.image.mobileImage.url,
				mobileWebP: result.data.data.image.mobileImage.webpUrl || undefined,
				alt: result.data.data.image.alt ?? '',
				link: result.data.data.image.link,
				trackingID: result.data.data.image.trackingID
			}
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getDiscoverSalon' } })
		}
		return DISCOVER_SALON_FALLBACKS()
	}
}

const getDiscoverService = async (client: AxiosInstance): Promise<CmsDiscoverServiceData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/discover-service', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data) {
			return DISCOVER_SERVICE_FALLBACKS()
		}

		return {
			title: result.data.data.title,
			subtitle: result.data.data.subtitle
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getDiscoverService' } })
		}
		return DISCOVER_SERVICE_FALLBACKS()
	}
}
const getTestimonials = async (client: AxiosInstance): Promise<CmsNpTestimonialsData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/testimonials', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data) {
			return TESTIMONIALS_FALLBACKS()
		}

		return {
			title: result.data.data.title,
			testimonials:
				result.data.data.testimonials?.map((testimonial, index) => ({
					ratingId: `review-${index}`,
					rating: testimonial.rating,
					name: testimonial.fullName,
					title: testimonial.title,
					text: testimonial.review
				})) || []
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getTestimonials' } })
		}
		return TESTIMONIALS_FALLBACKS()
	}
}

const getAppPromo = async (client: AxiosInstance): Promise<CmsAppPromoData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/app-promo', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data) {
			return APP_PROMO_FALLBACKS()
		}

		return {
			title: result.data.data.title,
			subtitle: result.data.data.subtitle,
			videoThumbnail: {
				desktop: result.data.data.videoThumbnail.desktopImage.url,
				desktopWebP: result.data.data.videoThumbnail.desktopImage.webpUrl || undefined,
				mobile: result.data.data.videoThumbnail.mobileImage.url,
				mobileWebP: result.data.data.videoThumbnail.mobileImage.webpUrl || undefined,
				alt: result.data.data.videoThumbnail.alt ?? '',
				link: result.data.data.videoThumbnail.link,
				trackingID: result.data.data.videoThumbnail.trackingID
			},
			background: {
				desktop: result.data.data.backgroundImage.desktopImage.url,
				mobile: result.data.data.backgroundImage.mobileImage.url,
				desktopWebP: result.data.data.backgroundImage.desktopImage.webpUrl || undefined,
				mobileWebP: result.data.data.backgroundImage.mobileImage.webpUrl || undefined
			},
			video: result.data.data.video?.url
				? {
						url: result.data.data.video.url,
						width: result.data.data.video.width,
						height: result.data.data.video.height,
						name: result.data.data.video.name
					}
				: undefined,
			tooltip: result.data.data.tooltip
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getAppPromo' } })
		}
		return APP_PROMO_FALLBACKS()
	}
}

const getAppFeature = async (client: AxiosInstance): Promise<CmsAppFeatureData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/app-feature', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data) {
			return APP_FEATURE_FALLBACKS()
		}

		return {
			title: result.data.data.title,
			subtitle: {
				desktop: result.data.data.subtitleDesktop,
				mobile: result.data.data.subtitleMobile
			},
			image1: {
				desktop: result.data.data.image1.desktopImage.url,
				desktopWebP: result.data.data.image1.desktopImage.webpUrl || undefined,
				mobile: result.data.data.image1.mobileImage.url,
				mobileWebP: result.data.data.image1.mobileImage.webpUrl || undefined,
				alt: result.data.data.image1.alt ?? '',
				link: result.data.data.image1.link,
				trackingID: result.data.data.image1.trackingID
			},
			image2: {
				desktop: result.data.data.image2.desktopImage.url,
				desktopWebP: result.data.data.image2.desktopImage.webpUrl || undefined,
				mobile: result.data.data.image2.mobileImage.url,
				mobileWebP: result.data.data.image2.mobileImage.webpUrl || undefined,
				alt: result.data.data.image2.alt ?? '',
				link: result.data.data.image2.link,
				trackingID: result.data.data.image2.trackingID
			},
			image3: {
				desktop: result.data.data.image3.desktopImage.url,
				desktopWebP: result.data.data.image3.desktopImage.webpUrl || undefined,
				mobile: result.data.data.image3.mobileImage.url,
				mobileWebP: result.data.data.image3.mobileImage.webpUrl || undefined,
				alt: result.data.data.image3.alt ?? '',
				link: result.data.data.image3.link,
				trackingID: result.data.data.image3.trackingID
			},
			image4: {
				desktop: result.data.data.image4.desktopImage.url,
				desktopWebP: result.data.data.image4.desktopImage.webpUrl || undefined,
				mobile: result.data.data.image4.mobileImage.url,
				mobileWebP: result.data.data.image4.mobileImage.webpUrl || undefined,
				alt: result.data.data.image4.alt ?? '',
				link: result.data.data.image4.link,
				trackingID: result.data.data.image4.trackingID
			},
			image5: {
				desktop: result.data.data.image5.desktopImage.url,
				desktopWebP: result.data.data.image5.desktopImage.webpUrl || undefined,
				mobile: result.data.data.image5.mobileImage.url,
				mobileWebP: result.data.data.image5.mobileImage.webpUrl || undefined,
				alt: result.data.data.image5.alt ?? '',
				link: result.data.data.image5.link,
				trackingID: result.data.data.image5.trackingID
			}
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getAppFeature' } })
		}
		return APP_FEATURE_FALLBACKS()
	}
}

const getNpForBusiness = async (client: AxiosInstance): Promise<CmsNpForBusinessData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/np-for-business', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data) {
			return NP_FOR_BUSINESSES_FALLBACKS()
		}

		return {
			image: {
				desktop: result.data.data.banner.desktopImage.url,
				desktopWebP: result.data.data.banner.desktopImage.webpUrl || undefined,
				mobile: result.data.data.banner.mobileImage.url,
				mobileWebP: result.data.data.banner.mobileImage.webpUrl || undefined,
				alt: result.data.data.banner.alt ?? '',
				link: result.data.data.banner.link,
				trackingID: result.data.data.banner.trackingID
			},
			backgroundImage: result.data.data.bannerBackground.url,
			backgroundImageWebP: result.data.data.bannerBackground.webpUrl || undefined
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getNpForBusiness' } })
		}
		return NP_FOR_BUSINESSES_FALLBACKS()
	}
}

const getNpStatistics = async (client: AxiosInstance): Promise<CmsNpStatisticsData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/np-statistics', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		const salonsStatistics = (result?.data?.data?.salonsStatistics as RichTextBlockNode[]) || []
		const reservationsStatistics = (result?.data?.data?.reservationsStatistics as RichTextBlockNode[]) || []
		const professionalsStatistics = (result?.data?.data?.professionalsStatistics as RichTextBlockNode[]) || []

		if (!result?.data?.data || !salonsStatistics.length || !reservationsStatistics.length || !professionalsStatistics.length) {
			return NP_STATISTICS_FALLBACKS()
		}

		return {
			salonsStatistics: {
				isRichText: true,
				data: salonsStatistics
			},
			reservationsStatistics: {
				isRichText: true,
				data: reservationsStatistics
			},
			professionalsStatistics: {
				isRichText: true,
				data: professionalsStatistics
			}
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getNpStatistics' } })
		}
		return NP_STATISTICS_FALLBACKS()
	}
}

const getHomeMetadata = async (client: AxiosInstance, countryCode: CountryCode): Promise<CmsHomeMetadata> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/seo/metadata/home', {
			params: { query: { countryCode } },
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})
		const data = result?.data?.data

		if (!data) {
			return HOME_METADATA_FALLBACKS()
		}
		return {
			metaTitle: data.metaData.metaTitle,
			metaDescription: data.metaData.metaDescription,
			metaKeywords: data.metaData.metaKeywords
		}
	} catch (_error) {
		return HOME_METADATA_FALLBACKS()
	}
}

const getSalonsListMetadata = async (client: AxiosInstance, query: SalonsListMetaQueryParams): Promise<CmsSalonsMetadata> => {
	try {
		const { countryCode } = query
		const result = await getReq(client, '/b2c/web/cms/seo/metadata/salons', {
			params: { query: { countryCode } },
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})
		const data = result?.data?.data?.metaData

		if (!data) {
			return SALONS_LIST_METADATA_FALLBACKS()
		}

		return {
			content: {
				isRichText: true,
				data: (data.content as unknown as RichTextBlockNode[]) || []
			},
			metaDescription: data.metaDescription,
			metaKeywords: data.metaKeywords,
			metaTitle: data.metaTitle,
			deactivatePage: data.deactivatePage,
			indexPage: data.indexPage,
			pageTitle: data.pageTitle,
			servicesContent: {
				isRichText: true,
				data: (data.services as unknown as RichTextBlockNode[]) || []
			},
			// NOTE: we are currently not supporting cities for the salons list page
			cities: undefined,
			citiesContent: undefined
		}
	} catch (error) {
		if (axios.isAxiosError(error) && error.response?.status === 404) {
			return Promise.reject(error)
		}
		return SALONS_LIST_METADATA_FALLBACKS()
	}
}

const getSalonsCategoryMetadata = async (
	client: AxiosInstance,
	query: SalonsCategoryMetaQueryParams,
	categoryName: string,
	categoryID: string
): Promise<CmsSalonsMetadata> => {
	const { countryCode, categorySlug, citySlug } = query

	try {
		const result = await getReq(client, '/b2c/web/cms/seo/metadata/salons', {
			params: { query: { countryCode, categorySlug, citySlug } },
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})
		const data = result?.data?.data?.metaData

		if (!data) {
			return SALONS_CATEGORY_METADATA_FALLBACKS(categoryID, categoryName, citySlug)
		}

		return {
			metaDescription: data.metaDescription,
			metaKeywords: data.metaKeywords,
			metaTitle: data.metaTitle,
			deactivatePage: data.deactivatePage,
			indexPage: data.indexPage,
			pageTitle: data.pageTitle,
			cities: data.cities,
			content: {
				isRichText: true,
				data: (data.content as unknown as RichTextBlockNode[]) || []
			},
			citiesContent: {
				isRichText: true,
				data: (data.citiesContent as unknown as RichTextBlockNode[]) || []
			},
			servicesContent: {
				isRichText: true,
				data: (data.services as unknown as RichTextBlockNode[]) || []
			}
		}
	} catch (error) {
		if (axios.isAxiosError(error) && error.response?.status === 404) {
			return Promise.reject(error)
		}
		return SALONS_CATEGORY_METADATA_FALLBACKS(categoryID, categoryName, citySlug)
	}
}

export const getCitiesByCategory = async (client: AxiosInstance, query: CitiesByCategoryParams): Promise<CmsCitiesByCategoryData> => {
	try {
		const { countryCode, categorySlug } = query

		const result = await getReq(client, '/b2c/web/cms/seo/cities/by-category', {
			params: { query: { countryCode, categorySlug } },
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		return {
			cities: result.data.data
		}
	} catch (error) {
		return Promise.reject(error)
	}
}

const getBanners = async (client: AxiosInstance): Promise<CmsBannersData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/banners', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data || result.data.data.images.length === 0) {
			return BANNERS_FALLBACKS()
		}

		return {
			banners: result.data.data.images.reduce<CmsBannersData['banners']>((acc, banner, index) => {
				return [
					...acc,
					{
						id: String(index),
						image: {
							desktop: banner.image.desktopImage.url,
							mobile: banner.image.mobileImage.url,
							desktopWebP: banner.image.desktopImage.webpUrl || undefined,
							mobileWebP: banner.image.mobileImage.webpUrl || undefined,
							link: banner.image.link,
							alt: banner.image.alt ?? '',
							trackingID: banner.image.trackingID
						},
						infoText: banner.infoText,
						backgroundColor: banner.backgroundColor,
						backgroundImage: banner.backgroundFile?.url
					}
				]
			}, [])
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getBanners' } })
		}
		return BANNERS_FALLBACKS()
	}
}

const getSalonListSmallBanner = async (client: AxiosInstance): Promise<CmsSalonListSmallBannerData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/salon-list-small-banner', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data) {
			return SALON_LIST_SMALL_BANNER_FALLBACKS()
		}

		return {
			image: {
				desktop: result.data.data.banner.desktopImage.url,
				desktopWebP: result.data.data.banner.desktopImage.webpUrl || undefined,
				mobile: result.data.data.banner.mobileImage.url,
				mobileWebP: result.data.data.banner.mobileImage.webpUrl || undefined,
				alt: result.data.data.banner.alt ?? '',
				link: result.data.data.banner.link,
				trackingID: result.data.data.banner.trackingID
			}
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getSalonListSmallBanner' } })
		}
		return SALON_LIST_SMALL_BANNER_FALLBACKS()
	}
}

const getLoginAndRegistrationBanner = async (client: AxiosInstance): Promise<CmsLoginAndRegistrationBannerData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/login-and-registration', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data || !result.data.data.circleImage.url) {
			return LOGIN_AND_REGISTRATION_BANNER_FALLBACKS()
		}

		return {
			image: {
				url: result.data.data.circleImage.url,
				urlWebP: result.data.data.circleImage.webpUrl || undefined,
				alt: '',
				link: undefined,
				trackingID: undefined
			}
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getLoginAndRegistrationBanner' } })
		}
		return LOGIN_AND_REGISTRATION_BANNER_FALLBACKS()
	}
}

const getParameterSelectionBanner = async (client: AxiosInstance): Promise<CmsParameterSelectionBannerData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/parameter-selection', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data) {
			return PARAMETER_SELECTION_BANNER_FALLBACKS()
		}

		return {
			image: {
				desktop: result.data.data.image.desktopImage.url,
				desktopWebP: result.data.data.image.desktopImage.webpUrl || undefined,
				mobile: result.data.data.image.mobileImage.url,
				mobileWebP: result.data.data.image.mobileImage.webpUrl || undefined,
				alt: result.data.data.image.alt ?? '',
				link: result.data.data.image.link,
				trackingID: result.data.data.image.trackingID
			}
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getParameterSelectionBanner' } })
		}
		return PARAMETER_SELECTION_BANNER_FALLBACKS()
	}
}

const getThankYouPageBanner = async (client: AxiosInstance): Promise<CmsThankYouPageBannerData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/thank-you-page-banner', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data) {
			return THANK_YOU_PAGE_BANNER_FALLBACKS()
		}

		return {
			image: {
				desktop: result.data.data.banner.desktopImage.url,
				desktopWebP: result.data.data.banner.desktopImage.webpUrl || undefined,
				mobile: result.data.data.banner.mobileImage.url,
				mobileWebP: result.data.data.banner.mobileImage.webpUrl || undefined,
				alt: result.data.data.banner.alt ?? '',
				link: result.data.data.banner.link,
				trackingID: result.data.data.banner.trackingID
			}
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getThankYouPageBanner' } })
		}
		return THANK_YOU_PAGE_BANNER_FALLBACKS()
	}
}

const getReservationDetailBanner = async (client: AxiosInstance): Promise<CmsReservationDetailData> => {
	try {
		const result = await getReq(client, '/b2c/web/cms/reservation-detail', {
			params: {},
			reqBody: {},
			customConfig: { displayNotification: false, autoInjectOrganizationID: false }
		})

		if (!result?.data?.data || !result?.data?.data.banner.url) {
			return RESERVATION_DETAIL_BANNER_FALLBACKS()
		}

		return {
			image: {
				desktop: result.data.data.banner.url,
				desktopWebP: result.data.data.banner.webpUrl || undefined,
				alt: result.data.data.alt ?? '',
				link: result.data.data.link,
				trackingID: result.data.data.trackingID
			}
		}
	} catch (error) {
		if (!axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { identifier: 'getReservationDetailBanner' } })
		}
		return RESERVATION_DETAIL_BANNER_FALLBACKS()
	}
}

const getReservationBookingModalCmsData = async (client: AxiosInstance): Promise<CmsReservationBookingModalData> => {
	const [loginAndRegistrationBannerData, parameterSelectionBannerData, thankYouPageBannerData] = await Promise.all([
		getLoginAndRegistrationBanner(client),
		getParameterSelectionBanner(client),
		getThankYouPageBanner(client)
	])

	return {
		loginAndRegistrationBannerData,
		parameterSelectionBannerData,
		thankYouPageBannerData
	}
}

const getHomepageCmsData = async (client: AxiosInstance, countryCode: CountryCode): Promise<CmsHomepageData> => {
	const [heroData, discoverSalonData, discoverServiceData, npTestimonialsData, appPromoData, appFeatureData, npForBusinessData, npStatisticsData, metaData] =
		await Promise.all([
			getHeroSection(client),
			getDiscoverSalon(client),
			getDiscoverService(client),
			getTestimonials(client),
			getAppPromo(client),
			getAppFeature(client),
			getNpForBusiness(client),
			getNpStatistics(client),
			getHomeMetadata(client, countryCode)
		])

	return {
		heroData,
		discoverSalonData,
		discoverServiceData,
		npTestimonialsData,
		appPromoData,
		appFeatureData,
		npForBusinessData,
		npStatisticsData,
		metaData
	}
}

const getSalonsListPageCmsData = async (client: AxiosInstance, query: SalonsListMetaQueryParams): Promise<CmsSalonsListPageData> => {
	const [appPromoData, salonListSmallBannerData, metaData] = await Promise.all([
		getAppPromo(client),
		getSalonListSmallBanner(client),
		getSalonsListMetadata(client, query)
	])

	return {
		appPromoData,
		salonListSmallBannerData,
		metaData
	}
}

const getSalonsCategoryPageCmsData = async (
	client: AxiosInstance,
	query: SalonsCategoryMetaQueryParams,
	categoryName: string,
	categoryID: string
): Promise<CmsSalonsCategoryPageData> => {
	const [appPromoData, bannersData, salonListSmallBannerData, metaData] = await Promise.all([
		getAppPromo(client),
		getBanners(client),
		getSalonListSmallBanner(client),
		getSalonsCategoryMetadata(client, query, categoryName, categoryID)
	])

	return {
		appPromoData,
		bannersData,
		salonListSmallBannerData,
		metaData
	}
}

const getMyReservationPageCmsData = async (client: AxiosInstance): Promise<CmsMyReservationPageData> => {
	const reservationDetailBannerData = await getReservationDetailBanner(client)

	return {
		reservationDetailBannerData
	}
}

const loadApiEndpoints = (client: AxiosInstance): CmsApiEndpoints => {
	return {
		getHomepageCmsData: (countryCode) => getHomepageCmsData(client, countryCode),
		getSalonsListPageCmsData: (query) => getSalonsListPageCmsData(client, query),
		getSalonsCategoryPageCmsData: (query, categoryName, categoryID) => getSalonsCategoryPageCmsData(client, query, categoryName, categoryID),
		getMyReservationPageCmsData: () => getMyReservationPageCmsData(client),
		getReservationBookingModalData: () => getReservationBookingModalCmsData(client),
		getCitiesByCategory: (query) => getCitiesByCategory(client, query)
	}
}

export default loadApiEndpoints
